<template>
  <Dialog
    :visible="visible"
    modal
    header="Invite a student"
    :style="{ width: '30rem' }"
    :breakpoints="{ '641px': '95vw' }"
    @update:visible="handleVisibilityChange"
  >
    <div class="p-fluid">
      <p class="text-sm text-700 mt-0 mb-4">
        Enter the student's email address to send an invitation to join the platform.
      </p>

      <label for="invite-student-email" class="font-medium mb-2">Student email address</label>
      <InputText
        id="invite-student-email"
        v-model="email"
        type="email"
        placeholder="student@example.com"
        :invalid="Boolean(emailError)"
        :disabled="loading"
        @blur="validateEmail"
        @keydown.enter.prevent="sendInvitation"
      />
      <Message v-if="emailError" severity="error" size="small" variant="simple" class="mt-2">
        {{ emailError }}
      </Message>
    </div>

    <template #footer>
      <div class="flex justify-content-between align-items-center w-full">
        <Button label="Cancel" icon="pi pi-times" text :disabled="loading" @click="closeDialog" />
        <Button
          label="Send invitation"
          icon="pi pi-send"
          class="teal-button"
          :loading="loading"
          @click="sendInvitation"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref } from 'vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { sendStudentInvitation } from '@/api/student';
import { sendTutorInvitation } from '@/api/tutor';
import { useAuthStore } from '@/stores/authStore';
import { notifyError, notifySuccess } from '@/utils/notification';

defineProps({
  visible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:visible']);
const authStore = useAuthStore();
const email = ref('');
const emailError = ref('');
const loading = ref(false);

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const validateEmail = () => {
  const normalizedEmail = email.value.trim();
  emailError.value = !normalizedEmail
    ? 'Email address is required.'
    : !isValidEmail(normalizedEmail)
      ? 'Please enter a valid email address.'
      : '';
  return !emailError.value;
};

const closeDialog = () => {
  email.value = '';
  emailError.value = '';
  emit('update:visible', false);
};

const handleVisibilityChange = (nextVisible) => {
  if (!nextVisible) closeDialog();
  else emit('update:visible', true);
};

const sendInvitation = async () => {
  if (!validateEmail()) return;

  const normalizedEmail = email.value.trim();
  const sendInvitationRequest = authStore.userType === 'student'
    ? sendStudentInvitation
    : authStore.userType === 'tutor'
      ? sendTutorInvitation
      : null;

  if (!sendInvitationRequest) {
    notifyError('Invitation failed', 'Only students and tutors can send invitations.');
    return;
  }

  loading.value = true;
  try {
    const response = await sendInvitationRequest(normalizedEmail);
    notifySuccess('Invitation sent', response?.data?.message || 'The invitation was sent successfully.');
    closeDialog();
  } catch (error) {
    notifyError('Invitation failed', error.response?.data?.message || 'Unable to send the invitation. Please try again.');
  } finally {
    loading.value = false;
  }
};
</script>